import React, { Fragment, useEffect, useState } from 'react';
import { PastVisitTableComponent, Visits } from './PastVisits';
import { batch, useDispatch, useSelector } from 'react-redux';
import { onCopy } from '../../../common/PastVisitUtils';
import PatientOverviewLabVitalsComponent from '../../LabVitalsHistoricalTable';
import {
    MEDICAL_RECORDS,
    PAST_VISIT_VITALS,
} from '../../../../pages/prescriptionPad/magicBox/magicBoxTableElements';
import Vitals from './Vitals';
import PatientOverViewBottomActionStrip from './padV2/PatientOverviewBottomActionStrip';
import MedicalRecordCard from '../../../../components/prescriptionPad/rightPanel/workspace/MedicalRecords';
import { getPatientByLocalId } from '../../../../store/idbHandler';
import { Patient } from '../../../../store/types/patient';
import { getRelatedProfilesData, getRxDoctor } from '../../../../store/selectors/prescriptionPad';
import { Relation } from '../../../../api/fetchPatientDirByDocOid';
import {
    cacheRelatedPatientMedicalRecords,
    cacheRelatedPatientPastVisits,
    cacheRelatedPatientPastVitals,
} from '../../../../store/actions/prescriptionPad';
import { track } from '../../../../tracking';
import { PtListItemViewOnly, PtListItemViewOnlyContent } from '../../../RelatedProfiles/PtListItem';

export const RELATED_PROFILES_CONTAINER_ID = 'RELATED_PROFILES_CONTAINER_ID';

const PPRelatedProfiles: React.FC<{ relations: Relation[] }> = ({ relations }) => {
    const [relationPatients, setRelationPatients] = useState<Patient[]>([]);
    const [activeRelationPatient, setActiveRelationPatient] = useState<Patient>();

    const getRelationPatients = async (relations: string[]): Promise<Patient[]> => {
        return await Promise.all(
            relations.map(async (l) => {
                return await getPatientByLocalId(l);
            }),
        );
    };

    const getRelationPatientIds = async () => {
        const relationPatientIds = relations.map((i) => i.pid);
        const relationPatients = await getRelationPatients(relationPatientIds);
        setRelationPatients(relationPatients);
        if (relationPatients.length > 0) {
            const firstPatient = relationPatients[0];
            setActiveRelationPatient(firstPatient);
            cacheRelatedPatientDetails(firstPatient.localId);
        }
    };

    useEffect(() => {
        getRelationPatientIds();
    }, [relations]);

    const dispatch = useDispatch();

    const onCopyElement = (visit: Visits, allSections: string[]) => {
        onCopy('PX', visit, allSections, dispatch);
    };

    const relatedProfilesData = useSelector(getRelatedProfilesData);
    const activeRelationPatientId = activeRelationPatient?.localId || '';
    const visits = relatedProfilesData?.[activeRelationPatientId]?.visits;
    const mr = relatedProfilesData?.[activeRelationPatientId]?.mr;
    const pastVitals = relatedProfilesData?.[activeRelationPatientId]?.pastVitals;
    const data = pastVitals?.data || {};
    const ptPastVitals = Object.keys(data).map((id) => ({
        id,
        ...data[id],
    }));
    const allPtIds = relatedProfilesData?.[activeRelationPatientId]?.allPtIds;

    const onClick = (pt: Patient) => {
        cacheRelatedPatientDetails(pt.localId);
        setActiveRelationPatient(pt);
        track('PP_RELATED_PROFILE_CLICK', { pid: pt.localId });
    };

    const cacheRelatedPatientDetails = (pid: string) => {
        const visits = relatedProfilesData?.[pid]?.visits;
        const mr = relatedProfilesData?.[pid]?.mr;
        const pastVitals = relatedProfilesData?.[pid]?.pastVitals;
        const isVisitsFetched = visits?.state === 'success';
        const isMRFetched = mr?.state === 'success';
        const isPastVitalsFetched = pastVitals?.state === 'success';
        batch(() => {
            !isVisitsFetched && dispatch(cacheRelatedPatientPastVisits(pid));
            !isMRFetched && dispatch(cacheRelatedPatientMedicalRecords(pid));
            !isPastVitalsFetched && dispatch(cacheRelatedPatientPastVitals(pid));
        });
    };

    const refreshRelatedPatientPastVisits = () => {
        dispatch(cacheRelatedPatientPastVisits(activeRelationPatientId));
    };

    const refreshRelatedPatientMedicalRecords = () => {
        dispatch(cacheRelatedPatientMedicalRecords(activeRelationPatientId));
    };

    const refreshRelatedPatientPastVitals = () => {
        dispatch(cacheRelatedPatientPastVitals(activeRelationPatientId));
    };
    const rxDoctor = useSelector(getRxDoctor);
    const rxDocId = rxDoctor?._id || '';

    return (
        <Fragment>
            <div
                id={RELATED_PROFILES_CONTAINER_ID}
                className="h-full relative overflow-hidden p-16 z-0 flex flex-col"
            >
                {relationPatients.length > 0 && (
                    <div className="px-6 flex space-x-16 pb-12 whitespace-nowrap overflow-auto">
                        {relationPatients.map((pt) => {
                            return (
                                <div
                                    className={`px-16 py-8 rounded-8 bg-white flex items-center ${
                                        activeRelationPatient?.localId === pt.localId
                                            ? 'shadow-tight-3 transform-gpu scale-103'
                                            : 'patient-details-visit-shadow hover:shadow-tight-3 hover:scale-103 transition-all transform-gpu duration-300 ease-in-out'
                                    } cursor-pointer`}
                                    onClick={() => onClick(pt)}
                                >
                                    <PtListItemViewOnly pt={pt} />
                                    <PtListItemViewOnlyContent pt={pt} className="pl-8" />
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="flex-1 overflow-auto">
                    <PastVisitTableComponent
                        isWrapper={true}
                        onCopy={onCopyElement}
                        visits={visits}
                        onRefresh={refreshRelatedPatientPastVisits}
                    />
                    <div className="h-auto lg:max-h-4/5 lg:min-h-4/5 lg:h-full lg:flex my-16">
                        <div className="h-full w-full min-w-60p">
                            <div className="h-full overflow-x-auto overflow-y-hidden relative  h-full lg:mr-16">
                                <div className="flex flex-col relative h-full rounded-8 col-span-12 bg-white overflow-auto">
                                    <PatientOverviewLabVitalsComponent
                                        patientId={activeRelationPatientId}
                                        ptPastVitals={ptPastVitals}
                                        dispatch={dispatch}
                                        fetchState={pastVitals?.state}
                                        onRefresh={refreshRelatedPatientPastVitals}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="h-full my-16 lg:my-0 overflow-auto min-w-40p">
                            <div
                                className="overflow-auto h-full rounded-8  w-full"
                                id={PAST_VISIT_VITALS}
                            >
                                <Vitals
                                    visits={visits}
                                    onRefresh={refreshRelatedPatientPastVisits}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="h-auto lg:min-h-4/5 my-16  rounded-8" id={MEDICAL_RECORDS}>
                        <MedicalRecordCard
                            mr={mr}
                            hideActionCTAs
                            ptId={activeRelationPatientId}
                            allPtIds={allPtIds}
                            profileFilterValue="all"
                            onRefresh={refreshRelatedPatientMedicalRecords}
                            docId={rxDocId}
                            dispatch={dispatch}
                        />
                    </div>
                    <div style={{ height: '256px' }}></div>
                </div>
            </div>
            <PatientOverViewBottomActionStrip />
        </Fragment>
    );
};

export default PPRelatedProfiles;
